/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { InputType } from '../vinhuset-business-sign-up';

export interface IBusinessSignUpInputProps {
    id?: string;
    type: InputType;
    value?: string;
    pattern?: string;
    className: string;
    disabled?: boolean;
    maxLength?: string;
    isRequired: boolean;
    fieldReference?: React.RefObject<HTMLInputElement>;
    placeholder?: string;
    onChange?(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void;
    onValidate?(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void;
}

export interface IBusinessSignUpInput {
    key: string;
    AddressItem: INodeProps;
    label: React.ReactNode;
    alert: React.ReactNode;
    input: React.ReactNode;
}

const GetMaxLength = (maxLength?: string): number | undefined => {
    if (maxLength) {
        const parsedMaxLength = Number.parseInt(maxLength, 10);
        if (!isNaN(parsedMaxLength)) {
            return parsedMaxLength;
        }
    }

    return undefined;
};

export const SignUpInput: React.FC<IBusinessSignUpInputProps> = ({
    id,
    type,
    value,
    pattern,
    className,
    disabled,
    maxLength,
    isRequired,
    onChange,
    onValidate: onValidation,
    fieldReference,
    placeholder
}) => {
    // Validate on blur
    if (type === InputType.TextArea) {
        return (
            <textarea
                id={id}
                name={id}
                value={value}
                className={`${className}-input-area ${className}-${id}`}
                aria-required={isRequired}
                disabled={disabled}
                maxLength={GetMaxLength(maxLength)}
                onChange={onChange}
                onBlur={onValidation}
                placeholder={placeholder}
            />
        );
    }

    return (
        <input
            id={id}
            type={type}
            value={value}
            className={`${className}-input ${className}-${id}`}
            pattern={pattern !== '' ? pattern : undefined}
            aria-required={isRequired}
            disabled={disabled}
            maxLength={GetMaxLength(maxLength)}
            onChange={onChange}
            onBlur={onValidation}
            ref={fieldReference}
            placeholder={placeholder}
        />
    );
};

export default SignUpInput;
